import { Box, Typography, Link, useTheme } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { AuroraGlow } from "../../components/auth/AuroraGlow";
import { AUTH_EASE, makeAuthColors } from "../../components/auth/authStyles";

// Public page (no auth) — it is the App Store listing's Support URL, so it
// must load logged-out and offer a real way to contact us. Styled on the
// auth family's aurora + glass surface.
const SupportPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const reduced = useReducedMotion();
  const { textPrimary, textSecondary, greenLink } = makeAuthColors(isDark);

  const linkSx = {
    color: greenLink,
    fontWeight: 600,
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  };

  const sectionTitleSx = {
    fontWeight: 700,
    fontSize: "1rem",
    color: textPrimary,
    mb: 1,
  };

  const bodySx = {
    color: textSecondary,
    fontSize: "0.9375rem",
    lineHeight: 1.6,
  };

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.palette.background.default,
        overflow: "hidden",
      }}
    >
      <AuroraGlow />

      <Box
        sx={{
          position: "relative",
          flex: 1,
          width: "100%",
          maxWidth: 640,
          mx: "auto",
          px: 3,
          pt: "calc(env(safe-area-inset-top, 0px) + 40px)",
          pb: "calc(env(safe-area-inset-bottom, 0px) + 32px)",
        }}
      >
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: AUTH_EASE }}
        >
          <Box sx={{ textAlign: "center", mb: 5 }}>
            <Link component={RouterLink} to="/sign-in" sx={{ display: "inline-block" }}>
              <Box
                component="img"
                src={isDark ? "/insightLongLogoWhite.svg" : "/insightLongLogoPrimary.svg"}
                alt="Insight Analytics"
                sx={{ height: 32, width: "auto", display: "block" }}
              />
            </Link>
          </Box>

          <Typography
            component="h1"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "2.25rem", sm: "2.5rem" },
              lineHeight: 1.1,
              letterSpacing: "-0.03em",
              color: textPrimary,
              textAlign: "center",
            }}
          >
            Support
          </Typography>
          <Typography
            sx={{
              mt: 1.5,
              mb: 4,
              fontSize: "1rem",
              lineHeight: 1.5,
              color: textSecondary,
              textAlign: "center",
            }}
          >
            Questions, problems, or feedback — we read everything and reply fast.
          </Typography>
        </motion.div>

        <motion.div
          initial={reduced ? false : { opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: AUTH_EASE, delay: 0.1 }}
        >
          <Box
            sx={{
              borderRadius: 4,
              backgroundColor: isDark
                ? "rgba(255, 255, 255, 0.06)"
                : "rgba(255, 255, 255, 0.5)",
              backdropFilter: "blur(12px)",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
              p: { xs: 3, sm: 4 },
            }}
          >
            <Typography sx={sectionTitleSx}>Contact us</Typography>
            <Typography sx={{ ...bodySx, mb: 3 }}>
              Email{" "}
              <Link href="mailto:support@insightanalytics.io" sx={linkSx}>
                support@insightanalytics.io
              </Link>{" "}
              and we will get back to you as soon as we can. Include the email address on
              your Insight account so we can look into things straight away.
            </Typography>

            <Typography sx={sectionTitleSx}>Billing and subscriptions</Typography>
            <Typography sx={{ ...bodySx, mb: 3 }}>
              Subscriptions purchased in the iOS app are billed by Apple and managed in your
              device's settings (Settings → your name → Subscriptions). Subscriptions purchased
              on the website are managed from Account Settings → Manage Plan. If anything looks
              wrong with a charge, email us and we'll sort it out.
            </Typography>

            <Typography sx={sectionTitleSx}>Account deletion</Typography>
            <Typography sx={bodySx}>
              You can permanently delete your account and data at any time from Account
              Settings, on the web or in the app.
            </Typography>
          </Box>

          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Link component={RouterLink} to="/terms" sx={{ color: textSecondary, textDecoration: "none", fontSize: "0.8125rem" }}>
              Terms of Service
            </Link>
            <Typography component="span" sx={{ mx: 1, color: textSecondary, fontSize: "0.8125rem" }}>
              ·
            </Typography>
            <Link component={RouterLink} to="/privacy-policy" sx={{ color: textSecondary, textDecoration: "none", fontSize: "0.8125rem" }}>
              Privacy Policy
            </Link>
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
};

export default SupportPage;
